/**
 * 📅 Scheduling — the three interview rounds under one top-level tab.
 *
 * First interviews (invite the shortlist), Case interviews (assemble a panel and
 * offer slots) and Final rounds (build the chain) are one job in a recruiter's
 * head: "get the next conversation booked". As five sibling tabs the strip
 * overflowed a laptop's width and 🏁 Final rounds sat off-screen behind a
 * sideways scroll, so the rounds moved in here as sub-navigation and the outer
 * strip came back to four.
 *
 * ── Why this is plain text, not pills ────────────────────────────────────────
 *
 * The first version used filled rounded pills, which is exactly what the summary
 * strip below uses for STATUS ("🟢 4 slots matchable now"). Two rows of small
 * rounded rectangles, one navigational and one informational, read as one row of
 * twelve chips — TA's report was that they were indistinguishable.
 *
 * So the rounds are now a text row: current one black and semibold with a thin
 * black underline, the others iron, separated by hairline dividers, counts as
 * bare coloured numbers. No boxes at all, which also answers the second half of
 * the request — fewer edges in the menu area. Chips are left to mean data.
 */
import type { ReactNode } from 'react';
import { cn } from '../../../../shared';
import { COLORS } from './format';
import { LiveChip } from './BoardChrome';
import { usePilotLock } from './PilotLock';

export type SchedulingSubTab = 'first' | 'case' | 'final';

export interface SchedulingSubCounts {
  first: number;
  case: number;
  final: number;
}

/** FINN CI: black for the current item, iron for the rest, pearl for rules. */
const BLACK = '#1A1A1A';
const IRON = '#707070';
const PEARL = '#E9EAEC';

export function SchedulingSubTabs({
  active,
  counts,
  finalRoundsUnavailable = false,
  onChange,
}: {
  active: SchedulingSubTab;
  counts: SchedulingSubCounts;
  /** The proxy predates the 🏁 endpoints — that round shows a dash, not a zero. */
  finalRoundsUnavailable?: boolean;
  onChange: (tab: SchedulingSubTab) => void;
}) {
  // Staggered launch: only Case interviews are live in the first wave, so only
  // that sub-tab is chipped. First rounds and Final rounds carry no chip.
  const { featureLive } = usePilotLock();
  const rounds: {
    id: SchedulingSubTab;
    label: string;
    count: number | null;
    color: string;
    live: boolean;
  }[] = [
    { id: 'first', label: 'First interviews', count: counts.first, color: COLORS.orangeText, live: featureLive('first') },
    { id: 'case', label: 'Case interviews', count: counts.case, color: COLORS.orangeText, live: featureLive('case') },
    {
      id: 'final',
      label: 'Final rounds',
      count: finalRoundsUnavailable ? null : counts.final,
      color: COLORS.blueText,
      live: featureLive('final'),
    },
  ];
  return (
    <div className="flex flex-wrap items-center" role="tablist" aria-label="Interview round">
      {rounds.map((round, i) => {
        const selected = round.id === active;
        return (
          <div key={round.id} className="flex items-center">
            {/* Hairline between rounds — one line each, not an outline around
                each one. */}
            {i > 0 && <span aria-hidden className="mx-3 h-3.5 w-px" style={{ backgroundColor: PEARL }} />}
            <button
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(round.id)}
              className={cn(
                'border-0 border-b-2 border-solid bg-transparent pb-1 text-sm transition-colors',
                selected ? 'font-semibold' : 'font-normal hover:opacity-80'
              )}
              style={{
                color: selected ? BLACK : IRON,
                borderBottomColor: selected ? BLACK : 'transparent',
              }}
            >
              {round.label}
              {round.count === null ? (
                <span
                  className="ml-1.5 text-xs"
                  style={{ color: IRON }}
                  title="Not available yet"
                >
                  —
                </span>
              ) : (
                <span className="ml-1.5 text-xs font-bold" style={{ color: round.color }}>
                  {round.count}
                </span>
              )}
              {round.live && <LiveChip />}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Sub-navigation plus whichever round is selected. */
export function SchedulingGroup({
  active,
  counts,
  finalRoundsUnavailable,
  onChange,
  children,
}: {
  active: SchedulingSubTab;
  counts: SchedulingSubCounts;
  finalRoundsUnavailable?: boolean;
  onChange: (tab: SchedulingSubTab) => void;
  children: ReactNode;
}) {
  return (
    // space-y-6, not 4: the rounds are navigation and the chips under them are
    // data, so the gap has to read as a section break rather than a list gap.
    <div className="space-y-6">
      <SchedulingSubTabs
        active={active}
        counts={counts}
        finalRoundsUnavailable={finalRoundsUnavailable}
        onChange={onChange}
      />
      {children}
    </div>
  );
}
